import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { C, card, label, btn, input, SIGNS, GLYPH, PLANET_GLYPH, ELEMENT_COLOR,
         LOT_COLORS, LOTS } from "../lib/theme";
const ELEM = ELEMENT_COLOR;

const MONTHS = ["January","February","March","April","May","June","July","August",
  "September","October","November","December"];
const AREAS = [["self","Self & body"],["money","Money & possessions"],
  ["communication","Siblings & communication"],["home","Home & family"],
  ["romance","Romance, creativity & children"],["work_health","Work & health"],
  ["partnership","Partnership & marriage"],["loss","Shared resources & loss"],
  ["travel","Travel & education"],["career","Career & reputation"],
  ["community","Friends & community"],["retreat","Solitude & retreat"]];
const ASPECT_SYM = { conjunction:"\u260C", sextile:"\u26B9", square:"\u25A1",
  trine:"\u25B3", opposition:"\u260D" };
const iso = (d) => d.toISOString().slice(0, 10);

export default function Calendar({ me }) {
  const [chart, setChart] = useState(null);
  const [noChart, setNoChart] = useState(false);
  const [ym, setYm] = useState({ y: 2026, m: 6 });               // July 2026
  const [activeLots, setActiveLots] = useState(["Spirit"]);
  const [periods, setPeriods] = useState({});                    // lot -> levels
  const [sky, setSky] = useState([]);
  const [profs, setProfs] = useState([]);
  const [returns, setReturns] = useState([]);
  const [logs, setLogs] = useState([]);
  const [openDay, setOpenDayRaw] = useState(null);
  const BLANK_FORM = { quality: "good", change: "beginning", life_area: "self", note: "" };
  const [form, setForm] = useState(BLANK_FORM);
  // fresh form per day popup — leftover state once made an old day look overwritten
  const setOpenDay = (d) => { setOpenDayRaw(d); setForm(BLANK_FORM); setErr(""); };
  const [err, setErr] = useState("");
  const [feedPanel, setFeedPanel] = useState(false);
  const [feedCfg, setFeedCfg] = useState({ max_level: 2, returns: true,
    ingresses: true, aspects: false });
  const [feedUrl, setFeedUrl] = useState(null);

  useEffect(() => {
    api.charts().then((cs) => {
      const self = cs.find((c) => c.is_self);
      self ? setChart(self) : setNoChart(true);
    });
  }, []);

  const frm = `${ym.y}-${String(ym.m + 1).padStart(2, "0")}-01`;
  const nextM = new Date(Date.UTC(ym.y, ym.m + 1, 1));
  const to = iso(nextM);

  useEffect(() => {
    if (!chart) return;
    Promise.all(activeLots.map((lot) => api.periods(chart.id, lot, frm, to)))
      .then((rs) => setPeriods(Object.fromEntries(rs.map((r) => [r.lot, r.levels]))));
    api.sky(frm, iso(new Date(nextM - 86400000))).then(setSky).catch(() => setSky([]));
    api.logs(frm, iso(new Date(nextM - 86400000))).then(setLogs);
    api.profections(chart.id, frm, iso(new Date(nextM - 86400000)))
      .then((p) => setProfs(p.profections)).catch(() => setProfs([]));
    api.returns(chart.id, frm, iso(new Date(nextM - 86400000)))
      .then((r) => setReturns(r.returns)).catch(() => setReturns([]));
  }, [chart, ym, activeLots.join()]);

  const daysInMonth = new Date(ym.y, ym.m + 1, 0).getDate();
  const firstDow = new Date(ym.y, ym.m, 1).getDay();
  const skyByDay = useMemo(() => Object.fromEntries(sky.map((d) => [d.day, d])), [sky]);
  const returnsByDay = useMemo(() => {
    const m = {};
    returns.forEach((r) => (m[r.at.slice(0, 10)] = [...(m[r.at.slice(0, 10)] || []), r]));
    return m;
  }, [returns]);
  const logsByDay = useMemo(() => {
    const m = {};
    logs.forEach((l) => (m[l.event_date] = [...(m[l.event_date] || []), l]));
    return m;
  }, [logs]);

  const stackFor = (dateStr) => {
    const out = {};
    for (const lot of activeLots) {
      const lv = periods[lot];
      if (!lv) continue;
      out[lot] = {};
      for (const l of ["1", "2", "3", "4"]) {
        const p = (lv[l] || []).find((p) => p.start.slice(0, 10) <= dateStr && dateStr < p.end.slice(0, 10) ||
          (p.start.slice(0,10) === dateStr));
        if (p) out[lot][l] = p;
      }
    }
    return out;
  };

  if (noChart) return <CreateChart onDone={(c) => { setChart(c); setNoChart(false); }} />;
  if (!chart) return <div style={{ color: C.faint }}>Loading your chart…</div>;

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <button style={btn(false)} onClick={() => setYm(ym.m ? { ...ym, m: ym.m - 1 } : { y: ym.y - 1, m: 11 })}>‹</button>
        <span style={{ fontFamily: C.displayFont, fontSize: 22, minWidth: 190 }}>
          {MONTHS[ym.m]} {ym.y}</span>
        <button style={btn(false)} onClick={() => setYm(ym.m === 11 ? { y: ym.y + 1, m: 0 } : { ...ym, m: ym.m + 1 })}>›</button>
        <span style={{ marginLeft: 12, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {LOTS.map((lot) => (
            <button key={lot} onClick={() => setActiveLots(activeLots.includes(lot)
                ? activeLots.filter((l) => l !== lot) : [...activeLots, lot])}
              style={{ background: activeLots.includes(lot) ? LOT_COLORS[lot] : C.surface2,
                color: activeLots.includes(lot) ? C.bg : C.dim, border: `1px solid ${C.line}`,
                borderRadius: 999, padding: "3px 11px", fontSize: 11.5, cursor: "pointer",
                fontFamily: "inherit", fontWeight: activeLots.includes(lot) ? 700 : 400 }}>
              {lot}</button>
          ))}
        </span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: C.faint }}>
          {chart.label} · {chart.asc_sign} rising · {chart.sect} chart ·{" "}
          <button onClick={() => setFeedPanel(!feedPanel)}
            style={{ all: "unset", cursor: "pointer", color: C.brass }}>calendar feed</button>
        </span>
      </div>

      {feedPanel && (
        <div style={{ ...card, marginBottom: 12 }}>
          <div style={label}>SUBSCRIBE IN GOOGLE / APPLE CALENDAR</div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center",
            fontSize: 12.5 }}>
            <span>Releases:{" "}
              {[["2", "L1/L2"], ["3", "+L3"], ["4", "+L4"]].map(([v, l]) => (
                <label key={v} style={{ marginRight: 8, cursor: "pointer" }}>
                  <input type="radio" checked={String(feedCfg.max_level) === v}
                    onChange={() => setFeedCfg({ ...feedCfg, max_level: Number(v) })}
                    style={{ accentColor: C.brass }} /> {l}</label>))}
            </span>
            {[["returns", "natal returns"], ["ingresses", "ingresses"],
              ["aspects", "sky aspects"]].map(([k, l]) => (
              <label key={k} style={{ cursor: "pointer" }}>
                <input type="checkbox" checked={feedCfg[k]}
                  onChange={(e) => setFeedCfg({ ...feedCfg, [k]: e.target.checked })}
                  style={{ accentColor: C.brass }} /> {l}</label>))}
            <button style={btn()} onClick={() =>
              api.createFeed({ chart_id: chart.id,
                config: { ...feedCfg, lots: activeLots } })
                .then((f) => setFeedUrl(location.origin + f.url))
                .catch((e) => setErr(e.message))}>
              Create feed for {activeLots.join(", ")}</button>
          </div>
          {feedUrl && (
            <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
              <input readOnly value={feedUrl} onFocus={(e) => e.target.select()}
                style={{ ...input, flexGrow: 1, fontFamily: "ui-monospace,monospace",
                  fontSize: 11 }} />
              <button style={btn(false)}
                onClick={() => navigator.clipboard?.writeText(feedUrl)}>Copy</button>
            </div>
          )}
          <div style={{ fontSize: 10.5, color: C.faint, marginTop: 6 }}>
            Anyone with the URL can read this feed — treat it like a password; revoke it
            from your profile to rotate. Refreshes twice daily with a rolling year ahead.
          </div>
        </div>
      )}
      {profs.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          {profs.map((p) => (
            <div key={p.age} style={{ background: C.surface, border: `1px solid ${C.brass}`,
              borderRadius: 6, padding: "6px 14px", fontSize: 11.5 }}>
              <span style={{ color: C.faint, letterSpacing: "0.1em", fontSize: 9.5 }}>
                ANNUAL PROFECTION · </span>
              age {p.age} · {p.house}{["th","st","nd","rd"][((p.house%100-20)%10>0&&(p.house%100-20)%10<4)?(p.house%10):(p.house===1?1:p.house===2?2:p.house===3?3:0)] || "th"} house ·{" "}
              <span style={{ color: ELEM(p.sign) }}>{GLYPH[p.sign]}</span> {p.sign} ·
              profection lord <b style={{ color: C.brass }}>{p.profection_lord}</b>
              {profs.length > 1 && <span style={{ color: C.faint }}> (from {p.start})</span>}
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
          <div key={d} style={{ fontSize: 10, letterSpacing: "0.12em", color: C.faint,
            textAlign: "center", padding: 4 }}>{d.toUpperCase()}</div>))}
        {Array.from({ length: firstDow }).map((_, i) => <div key={"b" + i} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const dateStr = `${ym.y}-${String(ym.m + 1).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`;
          const stack = stackFor(dateStr);
          const skyd = skyByDay[dateStr];
          const dayLogs = logsByDay[dateStr] || [];
          return (
            <div key={dateStr} onClick={() => setOpenDay(dateStr)}
              style={{ background: C.surface, border: `1px solid ${C.line}`, borderRadius: 6,
                minHeight: 86, padding: "5px 6px", cursor: "pointer", fontSize: 10.5 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: C.dim }}>{i + 1}</span>
                <span>{dayLogs.map((l, k) => <span key={k} title={l.note || l.life_area}
                  style={{ color: C.brass }}>◆</span>)}</span>
              </div>
              {Object.entries(stack).map(([lot, lv]) => (
                <div key={lot} style={{ display: "flex", gap: 3, alignItems: "baseline", marginTop: 2 }}
                  title={["1","2","3","4"].map((l) => lv[l] && `L${l} ${lv[l].sign}`).filter(Boolean).join(" · ")}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%",
                    background: LOT_COLORS[lot], display: "inline-block" }} />
                  {["1","2","3","4"].map((l) => lv[l] && (
                    <span key={l} style={{ color: ELEMENT_COLOR(lv[l].sign) }}>
                      {GLYPH[lv[l].sign]}{lv[l].lb ? "×" : ""}{lv[l].peak && l !== "1" ? "·" : ""}
                    </span>))}
                </div>
              ))}
              {(returnsByDay[dateStr] || []).map((r, k) => (
                <div key={"r" + k} title={r.kind} style={{ marginTop: 2, color: C.brass,
                  fontSize: 10.5 }}>⟲{PLANET_GLYPH[r.body]} {r.body} return</div>))}
              {skyd && <div style={{ marginTop: 2, color: C.dim }}>
                {skyd.ingresses.filter((g) => g.body !== "Moon").map((g, k) => (
                  <span key={k} title={`${g.body} → ${g.sign}${g.retrograde ? " (rx)" : ""}`}
                    style={{ marginRight: 3 }}>{PLANET_GLYPH[g.body]}{GLYPH[g.sign]}</span>))}
                {skyd.aspects.map((a, k) => (
                  <span key={"a" + k} title={`${a.bodies[0]} ${a.aspect} ${a.bodies[1]}`}
                    style={{ marginRight: 3, color: C.faint }}>{ASPECT_SYM[a.aspect]}</span>))}
              </div>}
            </div>
          );
        })}
      </div>

      {openDay && (
        <div onClick={() => setOpenDay(null)} style={{ position: "fixed", inset: 0,
          background: "rgba(6,8,16,0.7)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: 460, maxHeight: "84vh", overflow: "auto" }}>
            <div style={{ fontFamily: C.displayFont, fontSize: 20, marginBottom: 8 }}>
              {openDay}</div>
            {Object.entries(stackFor(openDay)).map(([lot, lv]) => (
              <div key={lot} style={{ fontSize: 12.5, marginBottom: 4 }}>
                <span style={{ color: LOT_COLORS[lot], fontWeight: 600 }}>{lot}</span>{": "}
                {["1","2","3","4"].map((l) => lv[l] &&
                  `L${l} ${lv[l].sign}${lv[l].peak ? " (peak)" : ""}${lv[l].lb ? " (LB)" : ""}`)
                  .filter(Boolean).join(" · ")}
              </div>
            ))}
            {(returnsByDay[openDay] || []).map((r, k) => (
              <div key={"rr" + k} style={{ fontSize: 12.5, color: C.brass, marginBottom: 4 }}>
                ⟲ {r.kind} exact at {r.at.slice(11)} UTC</div>))}
            {(logsByDay[openDay] || []).length > 0 && <>
              <div style={{ ...label, marginTop: 10 }}>LOGGED THIS DAY</div>
              {(logsByDay[openDay] || []).map((l) => {
                const qc = { good: C.earth, bad: C.rose, challenging: C.fire,
                  milestone: C.brass }[l.quality] || C.dim;
                return (
                  <div key={l.id} style={{ background: C.surface2, borderLeft: `3px solid ${qc}`,
                    borderRadius: 4, padding: "7px 10px", marginBottom: 6, fontSize: 12.5 }}>
                    <span style={{ color: qc, fontWeight: 600 }}>◆ {l.quality}</span>
                    <span style={{ color: C.faint }}> · {l.life_area}</span>
                    {l.note && <div style={{ color: C.text, marginTop: 3 }}>{l.note}</div>}
                  </div>);
              })}
            </>}
            <div style={{ ...label, marginTop: 12 }}>LOG A NEW EXPERIENCE</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
              <select value={form.quality} onChange={(e) => setForm({ ...form, quality: e.target.value })} style={input}>
                {["good","challenging","bad","neutral","milestone"].map((q) => <option key={q}>{q}</option>)}
              </select>
              <select value={form.change} onChange={(e) => setForm({ ...form, change: e.target.value })} style={input}>
                {["beginning","ending","continuation","unsure"].map((q) => <option key={q}>{q}</option>)}
              </select>
              <select value={form.life_area} onChange={(e) => setForm({ ...form, life_area: e.target.value })} style={input}>
                {AREAS.map(([slug, l]) => <option key={slug} value={slug}>{l}</option>)}
              </select>
            </div>
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Note (private, encrypted)" style={{ ...input, width: "100%", boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button style={btn()} onClick={() =>
                api.createLog({ chart_id: chart.id, event_date: openDay, ...form,
                  active_lot: activeLots[0] })
                  .then(() => { setOpenDay(null); api.logs(frm, to).then(setLogs); })
                  .catch((e) => setErr(e.message))}>Save</button>
              <button style={btn(false)} onClick={() => setOpenDay(null)}>Close</button>
            </div>
            {err && <div style={{ color: C.rose, fontSize: 12, marginTop: 8 }}>{err}</div>}
          </div>
        </div>
      )}
      <div style={{ fontSize: 10.5, color: C.faint, marginTop: 10 }}>
        Releases from /v1/charts/…/periods · sky from /v1/sky (seeded pipeline) ·
        × = Loosing of the Bond · dot = peak
      </div>
    </div>
  );
}

function CreateChart({ onDone }) {
  const [f, setF] = useState({ birth_local: "", lat: "", lon: "", place_label: "", label: "My chart" });
  const [err, setErr] = useState("");
  const [feedPanel, setFeedPanel] = useState(false);
  const [feedCfg, setFeedCfg] = useState({ max_level: 2, returns: true,
    ingresses: true, aspects: false });
  const [feedUrl, setFeedUrl] = useState(null);
  return (
    <div style={{ ...card, maxWidth: 460 }}>
      <div style={label}>CREATE YOUR CHART</div>
      <input placeholder="Birth (local): 1985-04-28T16:10" value={f.birth_local}
        onChange={(e) => setF({ ...f, birth_local: e.target.value })}
        style={{ ...input, width: "100%", boxSizing: "border-box", marginBottom: 6 }} />
      <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
        <input placeholder="Latitude" value={f.lat} onChange={(e) => setF({ ...f, lat: e.target.value })} style={{ ...input, flexGrow: 1 }} />
        <input placeholder="Longitude" value={f.lon} onChange={(e) => setF({ ...f, lon: e.target.value })} style={{ ...input, flexGrow: 1 }} />
      </div>
      <input placeholder="Place label" value={f.place_label}
        onChange={(e) => setF({ ...f, place_label: e.target.value })}
        style={{ ...input, width: "100%", boxSizing: "border-box", marginBottom: 10 }} />
      <button style={btn()} onClick={() =>
        api.createChart({ ...f, lat: Number(f.lat), lon: Number(f.lon), interactive: true })
          .then(onDone).catch((e) => setErr(e.message))}>
        Compute chart</button>
      {err && <div style={{ color: C.rose, fontSize: 12, marginTop: 8 }}>{err}</div>}
    </div>
  );
}
